import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Grid,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slide,
  Divider,
  Tooltip,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { motion } from 'framer-motion';

const Transition = React.forwardRef(function Transition(props, ref) {
  return (
    <Slide
      direction="up"
      ref={ref}
      timeout={{
        enter: 400,
        exit: 250,
      }}
      {...props}
    />
  );
});

function ResultCardAttribute({ title, description, attributes = [], onStatusChange }) {
  const [attributeStatuses, setAttributeStatuses] = useState(() =>
    attributes.map((attr) => ({ ...attr }))
  );
  const [open, setOpen] = useState(false);
  const [selectedAttrIndex, setSelectedAttrIndex] = useState(null);
  const [actionType, setActionType] = useState(null);

  const handleActionClick = (index, type) => {
    setSelectedAttrIndex(index);
    setActionType(type);
    setOpen(true);
  };

  const handleConfirm = () => {
    debugger;
    const updatedAttr = {
      ...attributeStatuses[selectedAttrIndex],
      status: actionType === 'approve' ? 'approved' : 'rejected',
    };

    setAttributeStatuses((prev) =>
      prev.map((attr, idx) => (idx === selectedAttrIndex ? updatedAttr : attr))
    );

    if (onStatusChange && typeof onStatusChange === 'function') {
      onStatusChange(updatedAttr.id, updatedAttr.status);
    }

    setOpen(false);
  };

  return (
    <>
      <Card
        component={motion.div}
        layout
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        sx={{
          borderRadius: 4,
          mt: 3,
          background: 'rgb(255 255 255 / 16%)',
          backdropFilter: 'blur(12px)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          transition: 'transform 0.3s ease',
          '&:hover': { transform: 'translateY(-4px)' },
        }}
      >
        <CardContent sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box>
              <Typography variant="h6" color="#1C2758" fontWeight={600}>
                {title}
              </Typography>
              {description && (
                <Typography variant="body2" color="#1C2758" mt={0.5}>
                  {description}
                </Typography>
              )}
            </Box>
          </Box>

          <Grid container rowSpacing={1} columnSpacing={6} sx={{ flexDirection: 'column' }}>
            {attributeStatuses.map((attr, index) => {
              const isApproved = attr.status === 'approved';
              const isRejected = attr.status === 'rejected';
              const isFromReltio = attr.source?.toLowerCase() === 'reltio';
              const isDomainNotFound = attr.domain?.toUpperCase() === 'NOT FOUND';
              const isDisabled = isFromReltio || isDomainNotFound || isApproved || isRejected;

              return (
                <React.Fragment key={attr.id || index}>
                  <Grid
                    item
                    xs={12}
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      pr: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="caption" color="#5A5A89" sx={{ fontWeight: 500 }}>
                        {attr.label}
                      </Typography>
                      <Typography variant="body2" color="#1C2758" fontWeight="500">
                        {attr.value}
                      </Typography>
                    </Box>

                    <Box display="flex" gap={1} alignItems="center">
                      {(isApproved || isRejected) && (
                        <Chip
                          label={isApproved ? 'Approved' : 'Rejected'}
                          icon={
                            isApproved ? (
                              <CheckCircleOutlineIcon sx={{ color: '#08AA66' }} />
                            ) : (
                              <CancelOutlinedIcon sx={{ color: '#D32F2F' }} />
                            )
                          }
                          size="small"
                          sx={{
                            ml: 2,
                            backgroundColor: 'transparent',
                            border: `1px solid ${isApproved ? '#08AA66' : '#D32F2F'}`,
                            color: isApproved ? '#08AA66' : '#D32F2F',
                            fontWeight: 500,
                          }}
                        />
                      )}

                      <Tooltip title={attr.domain || 'RELTIO'} arrow>
                        <Chip
                          label={
                            attr.source?.toUpperCase() === 'SCRAPER AGENT'
                              ? attr.domain
                              : 'MDM'
                          }
                          size="small"
                          sx={{
                            mt: 0.5,
                            ml: 2,
                            fontSize: 11,
                            minWidth: '7rem',
                            maxWidth: '7rem',
                            backgroundColor:
                              attr.source?.toUpperCase() === 'SCRAPER AGENT'
                                ? '#7c7c7c'
                                : '#1C2758',
                            color: '#fff',
                            fontWeight: 500,
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                          }}
                        />
                      </Tooltip>

                      <Button
                        size="small"
                        variant="text"
                        startIcon={
                          <CheckCircleOutlineIcon
                            sx={{ color: isDisabled ? 'grey' : '#08AA66' }}
                          />
                        }
                        sx={{ color: '#08AA66', fontWeight: 500 }}
                        onClick={() => handleActionClick(index, 'approve')}
                        disabled={isDisabled}
                      >
                        Approve
                      </Button>

                      <Button
                        size="small"
                        variant="text"
                        startIcon={
                          <CancelOutlinedIcon
                            sx={{ color: isDisabled ? 'grey' : '#D32F2F' }}
                          />
                        }
                        sx={{ color: '#D32F2F', fontWeight: 500 }}
                        onClick={() => handleActionClick(index, 'reject')}
                        disabled={isDisabled}
                      >
                        Reject
                      </Button>
                    </Box>
                  </Grid>
                  <Divider sx={{borderColor:'#0000001f'}} />
                </React.Fragment>
              );
            })}
          </Grid>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        TransitionComponent={Transition}
        keepMounted
        BackdropProps={{
          sx: {
            backgroundColor: 'rgb(0 0 0 / 9%)',
            backdropFilter: 'blur(7px)',
          },
        }}
        PaperProps={{
          component: motion.div,
          initial: { opacity: 0, y: 0 },
          animate: { opacity: 1, y: 0 },
          exit: { opacity: 0 },
          transition: { delay: 0.1, duration: 0.1 },
          sx: {
            background: 'rgba(255, 255, 255, 0.4)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, color: '#1C2758' }}>
          {actionType === 'approve' ? 'Confirm Approval' : 'Confirm Rejection'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="#1C2758" mb={1}>
            Are you sure you want to{' '}
            <strong>{actionType === 'approve' ? 'approve' : 'reject'}</strong>{' '}
            the following data?
          </Typography>

          <Box
            mt={2}
            p={2}
            border="1px solid rgba(28, 39, 88, 0.3)"
            borderRadius={2}
            bgcolor="rgba(255,255,255,0.5)"
            sx={{ backdropFilter: 'blur(6px)' }}
          >
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#5A5A89' }}>
              Label:
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#1C2758', mb: 1 }}>
              {attributeStatuses[selectedAttrIndex]?.label || '—'}
            </Typography>

            <Typography variant="caption" sx={{ fontWeight: 600, color: '#5A5A89' }}>
              Value:
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#1C2758', mb: 1 }}>
              {attributeStatuses[selectedAttrIndex]?.value || '—'}
            </Typography>

            <Typography variant="caption" sx={{ fontWeight: 600, color: '#5A5A89', mt: 2 }}>
              Source Domain:
            </Typography>
            <Chip
              label={attributeStatuses[selectedAttrIndex]?.domain || 'Unknown Domain'}
              size="small"
              sx={{
                backgroundColor: '#1C2758',
                color: '#fff',
                fontWeight: 500,
                fontSize: 12,
                ml: 1,
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)} sx={{ fontWeight: 500 }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            sx={{
              color: actionType === 'approve' ? '#08AA66' : '#D32F2F',
              fontWeight: 600,
            }}
          >
            {actionType === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ResultCardAttribute;
